import { Modal } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApimHandler } from '@api/apim-handler';
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import { filterCatalogEndpoints, resolveGroupKeyForBackend } from '@pages/api-portal/utils';
import { ApiEndpoint, ApiGroup } from '@pages/api-portal/types';
import { EndpointFormData } from './endpoint-dialog';

/*
 * Encapsulates data fetching, filters, and CRUD handlers for the API Endpoints manager.
 */
export function useEndpointsManager() {
    const [endpoints, setEndpoints] = useState<ApiEndpoint[]>([]);
    const [groups, setGroups] = useState<ApiGroup[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const [searchQuery, setSearchQuery] = useState('');
    const [filterGroup, setFilterGroup] = useState<string | null>(null);
    const [filterMethod, setFilterMethod] = useState<string | null>(null);

    const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
    const [selectedEndpoint, setSelectedEndpoint] = useState<ApiEndpoint | null>(null);

    const fetchAll = useCallback(async () => {
        setIsLoading(true);
        try {
            const [endpointsRes, groupsRes] = await Promise.all([
                ApimHandler.fetchAllEndpoints(),
                ApimHandler.fetchApiGroups(),
            ]);
            setEndpoints(endpointsRes.data);
            setGroups(groupsRes.data);
        } catch {
            errorAlert('Failed to load API endpoints.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const filteredEndpoints = useMemo(
        () =>
            filterCatalogEndpoints(endpoints, {
                searchQuery,
                selectedGroup: filterGroup,
                selectedMethods: filterMethod ? [filterMethod] : [],
            }),
        [endpoints, searchQuery, filterGroup, filterMethod],
    );

    const openCreateDialog = useCallback(() => {
        setSelectedEndpoint(null);
        setIsFormDialogOpen(true);
    }, []);

    const openEditDialog = useCallback((endpoint: ApiEndpoint) => {
        setSelectedEndpoint(endpoint);
        setIsFormDialogOpen(true);
    }, []);

    /*
     * Creates or updates an endpoint, resolving the form's selected group id back to the
     * backend's groupKey and splitting the comma-separated tags input into an array.
     */
    const handleSubmit = useCallback(
        async (data: EndpointFormData) => {
            const tags = data.tags
                .split(',')
                .map((t) => t.trim())
                .filter(Boolean);
            const groupKey = resolveGroupKeyForBackend(data.group, groups);

            if (selectedEndpoint) {
                await ApimHandler.updateApiEndpoint(selectedEndpoint.id, {
                    endpointKey: data.endpointKey,
                    name: data.name,
                    description: data.description,
                    method: data.method,
                    path: data.path,
                    version: data.version,
                    group: groupKey,
                    status: data.status,
                    tags,
                });
                successAlert('Endpoint updated.');
            } else {
                await ApimHandler.createApiEndpoint({
                    endpointKey: data.endpointKey,
                    name: data.name,
                    description: data.description,
                    method: data.method,
                    path: data.path,
                    version: data.version,
                    group: groupKey,
                    status: data.status,
                    tags,
                    responses: [],
                });
                successAlert('Endpoint created.');
            }
            setIsFormDialogOpen(false);
            fetchAll();
        },
        [selectedEndpoint, groups, fetchAll],
    );

    const handleDeleteRequest = useCallback(
        (endpoint: ApiEndpoint) => {
            Modal.confirm({
                className: 'apimModal',
                title: 'Delete API Endpoint',
                content: `Are you sure you want to delete "${endpoint.name}"? This action cannot be undone.`,
                okText: 'Delete',
                cancelText: 'Cancel',
                okButtonProps: { style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' } },
                onOk: async () => {
                    try {
                        await ApimHandler.deleteApiEndpoint(endpoint.id);
                        successAlert('Endpoint deleted.');
                        fetchAll();
                    } catch {
                        errorAlert('Failed to delete endpoint.');
                    }
                },
            });
        },
        [fetchAll],
    );

    return {
        groups,
        isLoading,
        searchQuery,
        setSearchQuery,
        filterGroup,
        setFilterGroup,
        filterMethod,
        setFilterMethod,
        filteredEndpoints,
        isFormDialogOpen,
        setIsFormDialogOpen,
        selectedEndpoint,
        openCreateDialog,
        openEditDialog,
        handleSubmit,
        handleDeleteRequest,
    };
}
